import type {
	MediaDeviceInfoModel,
	SoftphoneCallStateModel,
	SoftphoneLeaderRole,
	SoftphoneQualityWarningModel,
} from '@supply-mover-2-0/core';
import type {
	CallerIdMatchResultDto,
	TransferIncomingContextDto,
} from '@supply-mover-2-0/shared';
import { create } from 'zustand';

/**
 * View-model snapshot of the active call. The actual `SoftphoneCallModel`
 * instance lives in the SoftphoneProvider's React ref (it has
 * side-effectful internals — event listeners, SDK handles — that don't
 * fit cleanly in a Zustand store). The Provider mirrors lifecycle
 * events into this view shape so consumers re-render reactively.
 *
 * `callerIdMatch` is populated by the inbound TwiML's custom params
 * (caller-ID match runs server-side before the call rings the rep).
 * Carries best + alternatives + confidence so the call pop can render
 * a multi-match chooser when the caller hits more than one record.
 *
 * `persistedCallId` is the DB id of the `call` row written at TwiML
 * route time; the call pop uses it for follow-up REST calls
 * (re-attribute, hold/transfer, notes).
 */
export interface SoftphoneCallView {
	id: string;
	direction: 'inbound' | 'outbound';
	fromE164: string | null;
	toE164: string | null;
	state: SoftphoneCallStateModel;
	isMuted: boolean;
	isOnHold: boolean;
	callerIdMatch: CallerIdMatchResultDto | null;
	persistedCallId: number | null;
	recordingActive: boolean;
	/**
	 * Non-null when this call is the target leg of a transfer initiated
	 * by another rep — drives the "Transfer from X" banner on the call
	 * pop. Populated via the incoming-context fetch.
	 */
	transferContext: TransferIncomingContextDto | null;
	startedAt: number;
	connectedAt: number | null;
	activeQualityWarnings: ReadonlyArray<SoftphoneQualityWarningModel>;
}

/**
 * Single source of truth for the softphone UI. Lives in libs/web-ui so
 * that the Navigation surface, the floating call-pop modal, and the
 * /inbox personal-inbox page (modules/ai-assistant + future siblings)
 * all read the same state.
 *
 * The `_`-prefixed setters are internal — only the SoftphoneProvider
 * is meant to call them. Components should subscribe through
 * `useSoftphone` / `useIncomingCall` / `useCallControls` hooks and
 * issue imperative actions via the Provider's context.
 */
/**
 * Post-call wrap-up state. Populated when an active call ends with
 * the rep having actually engaged (i.e. it transitioned to
 * `connected` at some point — declined / no-answer don't qualify).
 * Drives the `<CallWrapUpPanel>`. Cleared when the rep saves or
 * dismisses, or when a new call starts.
 */
export interface SoftphoneWrapUpModel {
	dbCallId: number;
	partnerLabel: string | null;
	durationSeconds: number;
	startedAt: number;
	endedAt: number;
	/** Matched record snapshot at the time the call ended (DD.9.4).
	 *  Drives the wrap-up panel's "Save activity to {name}" toggle.
	 *  Null when the call had no matched client / vendor. The rep can
	 *  re-attribute via the call pop earlier in the call; whatever
	 *  attribution is on the call row when wrap-up fires is what
	 *  shows here. */
	matchType: 'client' | 'vendor' | null;
	matchEntityId: number | null;
	matchDisplayName: string | null;
}

/**
 * Surfaces the rep-relevant context of a call that just ended so a
 * toast / sticky banner can show "why" for a few seconds. Cleared
 * automatically by `_setCurrentCall` when the next call starts; the
 * toast component sets a timeout to clear it sooner for the
 * "smooth completion" cases.
 */
export interface SoftphoneLastDisconnectModel {
	at: number;
	/** Disconnect reason code (provider-agnostic). */
	code: string;
	/** Free-form message — usually safe to render. */
	message: string;
	/** Whether the user-visible toast should be in error tone. */
	isError: boolean;
	/** displayName from the matched record, when there was one. */
	partnerLabel: string | null;
}

/**
 * Manual presence picker state (DD.13). One of four MS-Teams-flavored
 * statuses. `null` while the initial fetch is in flight or when the
 * rep has no `softphone_number_config` (presence is moot without a
 * number to be unavailable on — picker hides itself in that case).
 *
 * Lifted from the picker component into shared state because the
 * call-pop branch (DD.13.b) needs to read it: 'busy' suppresses the
 * full call-pop and renders a small toast instead so the rep stays
 * focused on whatever they were doing.
 */
export type SoftphonePresenceStatus = 'available' | 'busy' | 'dnd' | 'offline';

export type SoftphoneCoachingMode = 'listen' | 'whisper' | 'barge';

/**
 * Tier 2 coaching state visible to the rep on their own active call.
 * Trimmed to mode-only — coach identity isn't surfaced (matches the
 * server-side rep-coaching-state endpoint shape). Empty array means
 * no active coaching; populated array means at least one manager is
 * coaching (multiple managers on the same call is rare but allowed).
 *
 * Cleared on every `_setCurrentCall(null)` so the badge auto-hides
 * when a call ends, even if the WS hint hasn't arrived yet.
 */
export interface SoftphoneRepCoachingStateModel {
	coachings: ReadonlyArray<{ mode: SoftphoneCoachingMode }>;
}

/**
 * Tier 2 coaching session where the calling user IS the coach.
 * Populated when the manager has at least one in-flight coaching
 * session; drives the `<CoachingToolbar>` (mode display, mode-switch
 * buttons, end button) and tells `<ActiveCallBar>` to suppress the
 * normal call controls (mute / hold / transfer would all do the
 * wrong thing on a coaching leg).
 *
 * Multi-session is allowed but rare; for v1 we surface the FIRST
 * active session — `useManagerCoachingSession` returns the oldest
 * by `startedAt`, matching what `listActiveSessionsForCoach` returns
 * already-sorted.
 */
export interface SoftphoneManagerCoachingSessionModel {
	sessionId: number;
	dbCallId: number;
	mode: SoftphoneCoachingMode;
	startedAt: string;
}

export interface SoftphoneStoreStateModel {
	// Tab role + device registration
	role: SoftphoneLeaderRole;
	isRegistered: boolean;

	/** Current manual presence (DD.13). Null = unloaded / no number. */
	manualPresenceStatus: SoftphonePresenceStatus | null;

	// Active call (null when idle)
	currentCall: SoftphoneCallView | null;

	/** Most-recent disconnect, captured at the moment the call ended.
	 *  The disconnection-reason toast reads this and clears itself
	 *  after a few seconds. Null when there's no recent disconnect to
	 *  surface or when the rep has already dismissed it. */
	lastDisconnect: SoftphoneLastDisconnectModel | null;

	/** Live device-level connection health.
	 *   - 'online' — registered, signaling channel up
	 *   - 'reconnecting' — SDK reported a transient drop and is
	 *     re-establishing; UI surfaces a banner so the rep knows the
	 *     audio dip is being recovered (vs the call being dead)
	 *   - 'offline' — disposed or the leader tab has unregistered */
	connectionState: 'online' | 'reconnecting' | 'offline';

	/** Active wrap-up state — set when a connected call ends, cleared
	 *  when the rep saves the disposition or dismisses the panel. */
	wrapUp: SoftphoneWrapUpModel | null;

	/** Second inbound call that arrived while `currentCall` is busy.
	 *  The Provider populates this when a new `incomingCall` event
	 *  fires with an existing call active; the call-waiting banner
	 *  surfaces it ("New call from Big Lead Co — End current to
	 *  answer?"). Cleared when the rep answers it (after ending the
	 *  current call), declines it, or the caller hangs up. */
	pendingCall: SoftphoneCallView | null;

	/** Mirror of whether the leader tab currently has an active call,
	 *  driven by the `leader-state` bus message. Lets the
	 *  `<CallActiveInOtherTabBanner>` distinguish "phone idle in
	 *  another tab" from "call in progress in another tab" —
	 *  important because clicking "Switch" while a call is active
	 *  will be denied (Phase 2.4+ Conference architecture is what
	 *  enables real handoff). */
	leaderHasActiveCall: boolean;

	/** Set when the leader tab posts `leadership-denied` in response
	 *  to a `request-leadership` from this tab. Drives a transient
	 *  "Can't switch — leader has an active call" toast in the
	 *  banner. Auto-cleared after a few seconds. */
	leadershipDeniedAt: number | null;

	/** Tier 2 coaching: rep-side badge state on the rep's own active
	 *  call. Refetched whenever the coaching push hint fires for
	 *  this user, plus a one-shot fetch on call-start. Cleared
	 *  automatically on `_setCurrentCall(null)`. */
	repCoachingState: SoftphoneRepCoachingStateModel;

	/**
	 * RAY BAUM §506 — when set, the rep just attempted to dial out
	 * but their E911 attestation is expired (and the target wasn't
	 * 911 / 933 / 988). The `<E911ReAttestationModal>` reads this
	 * state and renders. On Confirm → POST attest → clear this state
	 * → the modal retries the dial against the carried `toE164` /
	 * `fromCallerId`. On Cancel → clear; the rep needs to fix it from
	 * Settings or click dial again.
	 *
	 * Null when no gated dial is pending. The provider's `dial`
	 * action is the only writer.
	 */
	e911GatedDial: {
		toE164: string;
		fromCallerId: string | null;
		address: {
			friendlyName: string | null;
			street1: string;
			street2: string | null;
			city: string;
			region: string;
			postalCode: string;
			isoCountry: string;
		} | null;
		hasAddress: boolean;
	} | null;

	/** Tier 2 coaching: the calling user's own active coaching
	 *  session if any — null when not actively coaching anyone.
	 *  Drives the `<CoachingToolbar>` and tells `<ActiveCallBar>` to
	 *  suppress normal call controls (the manager's "current call"
	 *  IS the coaching leg; mute/hold/transfer don't apply). */
	activeCoachingSession: SoftphoneManagerCoachingSessionModel | null;

	// Audio devices
	inputDevices: ReadonlyArray<MediaDeviceInfoModel>;
	outputDevices: ReadonlyArray<MediaDeviceInfoModel>;
	selectedInputDeviceId: string | null;
	selectedOutputDeviceId: string | null;

	// Internal mutators — Provider-only
	_setRole: (role: SoftphoneLeaderRole) => void;
	_setRegistered: (registered: boolean) => void;
	_setManualPresenceStatus: (status: SoftphonePresenceStatus | null) => void;
	_setCurrentCall: (call: SoftphoneCallView | null) => void;
	_patchCurrentCall: (patch: Partial<SoftphoneCallView>) => void;
	_setLastDisconnect: (disconnect: SoftphoneLastDisconnectModel | null) => void;
	_setConnectionState: (state: 'online' | 'reconnecting' | 'offline') => void;
	_setWrapUp: (wrapUp: SoftphoneWrapUpModel | null) => void;
	_setPendingCall: (call: SoftphoneCallView | null) => void;
	_setLeaderHasActiveCall: (hasActiveCall: boolean) => void;
	_setLeadershipDeniedAt: (at: number | null) => void;
	_setRepCoachingState: (state: SoftphoneRepCoachingStateModel) => void;
	_setE911GatedDial: (gated: SoftphoneStoreStateModel['e911GatedDial']) => void;
	_setActiveCoachingSession: (
		session: SoftphoneManagerCoachingSessionModel | null,
	) => void;
	_addQualityWarning: (warning: SoftphoneQualityWarningModel) => void;
	_clearQualityWarning: (code: string) => void;
	/**
	 * Local-only re-pick from the alternatives list — promotes the
	 * record at `rank` to `best` and demotes the previous best back
	 * into alternatives. UI affordance only; the backend
	 * `softphone_caller_id_attribution` write is out of scope here
	 * (lives in the wrap-up flow).
	 */
	_selectCallerIdMatchAlternative: (rank: number) => void;
	_setDevices: (
		inputs: ReadonlyArray<MediaDeviceInfoModel>,
		outputs: ReadonlyArray<MediaDeviceInfoModel>,
	) => void;
	_setSelectedInput: (id: string | null) => void;
	_setSelectedOutput: (id: string | null) => void;
	_reset: () => void;
}

const INITIAL_STATE = {
	role: 'follower' as SoftphoneLeaderRole,
	isRegistered: false,
	manualPresenceStatus: null as SoftphonePresenceStatus | null,
	currentCall: null as SoftphoneCallView | null,
	lastDisconnect: null as SoftphoneLastDisconnectModel | null,
	connectionState: 'offline' as 'online' | 'reconnecting' | 'offline',
	wrapUp: null as SoftphoneWrapUpModel | null,
	pendingCall: null as SoftphoneCallView | null,
	leaderHasActiveCall: false,
	leadershipDeniedAt: null as number | null,
	repCoachingState: { coachings: [] } as SoftphoneRepCoachingStateModel,
	activeCoachingSession: null as SoftphoneManagerCoachingSessionModel | null,
	e911GatedDial: null as SoftphoneStoreStateModel['e911GatedDial'],
	inputDevices: [] as ReadonlyArray<MediaDeviceInfoModel>,
	outputDevices: [] as ReadonlyArray<MediaDeviceInfoModel>,
	selectedInputDeviceId: null as string | null,
	selectedOutputDeviceId: null as string | null,
};

export const useSoftphoneStore = create<SoftphoneStoreStateModel>((set) => ({
	...INITIAL_STATE,

	_setRole: (role: SoftphoneLeaderRole): void => {
		set({ role });
	},

	_setRegistered: (registered: boolean): void => {
		set({ isRegistered: registered });
	},

	_setManualPresenceStatus: (status: SoftphonePresenceStatus | null): void => {
		set({ manualPresenceStatus: status });
	},

	_setCurrentCall: (call: SoftphoneCallView | null): void => {
		set((state) => ({
			currentCall: call,
			// New call starting → drop any stale disconnect toast so
			// the rep doesn't see "previous call ended" overlapping
			// the active-call bar.
			lastDisconnect: call != null ? null : state.lastDisconnect,
			// Tier 2 coaching: badge is per-call. Clearing on call-end
			// makes the badge auto-hide even if the WS hint hasn't
			// caught up yet; clearing on call-start gives a clean slate
			// for the next call's coaching fetch.
			repCoachingState: { coachings: [] },
		}));
	},

	_setRepCoachingState: (state: SoftphoneRepCoachingStateModel): void => {
		set({ repCoachingState: state });
	},

	_setE911GatedDial: (
		gated: SoftphoneStoreStateModel['e911GatedDial'],
	): void => {
		set({ e911GatedDial: gated });
	},

	_setActiveCoachingSession: (
		session: SoftphoneManagerCoachingSessionModel | null,
	): void => {
		set({ activeCoachingSession: session });
	},

	_setLastDisconnect: (
		disconnect: SoftphoneLastDisconnectModel | null,
	): void => {
		set({ lastDisconnect: disconnect });
	},

	_setConnectionState: (state: 'online' | 'reconnecting' | 'offline'): void => {
		set({ connectionState: state });
	},

	_setWrapUp: (wrapUp: SoftphoneWrapUpModel | null): void => {
		set({ wrapUp });
	},

	_setPendingCall: (call: SoftphoneCallView | null): void => {
		set({ pendingCall: call });
	},

	_setLeaderHasActiveCall: (hasActiveCall: boolean): void => {
		set({ leaderHasActiveCall: hasActiveCall });
	},

	_setLeadershipDeniedAt: (at: number | null): void => {
		set({ leadershipDeniedAt: at });
	},

	_patchCurrentCall: (patch: Partial<SoftphoneCallView>): void => {
		set((state) => {
			if (state.currentCall == null) return {};
			return { currentCall: { ...state.currentCall, ...patch } };
		});
	},

	_addQualityWarning: (warning: SoftphoneQualityWarningModel): void => {
		set((state) => {
			if (state.currentCall == null) return {};
			// Replace any existing warning with the same code, then add the new one.
			const filtered = state.currentCall.activeQualityWarnings.filter(
				(w) => w.code !== warning.code,
			);
			return {
				currentCall: {
					...state.currentCall,
					activeQualityWarnings: [...filtered, warning],
				},
			};
		});
	},

	_clearQualityWarning: (code: string): void => {
		set((state) => {
			if (state.currentCall == null) return {};
			return {
				currentCall: {
					...state.currentCall,
					activeQualityWarnings: state.currentCall.activeQualityWarnings.filter(
						(w) => w.code !== code,
					),
				},
			};
		});
	},

	_selectCallerIdMatchAlternative: (rank: number): void => {
		set((state) => {
			const call = state.currentCall;
			if (call == null || call.callerIdMatch == null) return {};
			const match = call.callerIdMatch;
			const promoted = match.alternatives.find((a) => a.rank === rank);
			if (promoted == null) return {};
			const remainingAlternatives = match.alternatives.filter(
				(a) => a.rank !== rank,
			);
			const newAlternatives =
				match.best != null
					? [match.best, ...remainingAlternatives].sort(
							(a, b) => a.rank - b.rank,
						)
					: remainingAlternatives;
			return {
				currentCall: {
					...call,
					callerIdMatch: {
						...match,
						best: promoted,
						alternatives: newAlternatives,
					},
				},
			};
		});
	},

	_setDevices: (
		inputs: ReadonlyArray<MediaDeviceInfoModel>,
		outputs: ReadonlyArray<MediaDeviceInfoModel>,
	): void => {
		set({ inputDevices: inputs, outputDevices: outputs });
	},

	_setSelectedInput: (id: string | null): void => {
		set({ selectedInputDeviceId: id });
	},

	_setSelectedOutput: (id: string | null): void => {
		set({ selectedOutputDeviceId: id });
	},

	_reset: (): void => {
		set({ ...INITIAL_STATE });
	},
}));
